// ── Adapter Supabase do IFlowConfigRepository ─────────────────────
// Implementa a porta do módulo Activity (Flow Config) usando o cliente
// Supabase. É a ÚNICA camada que conhece as tabelas do banco para o fluxo.

import { getSupabase } from '@infra/supabase/client';
import type {
  IFlowConfigRepository,
  StageRoleInfo,
  PipelineFull,
  CardOnCompleteInfo,
  StageName,
  LeadCardCreationInfo,
} from '@modules/activity';

export class SupabaseFlowConfigRepository implements IFlowConfigRepository {
  // ── Stage roles ───────────────────────────────────────────────
  getStageByRole: IFlowConfigRepository['getStageByRole'] = async (pipelineId, role) => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data, error } = await supabase
      .from('pipeline_stages')
      .select('id, name, color, stage_role')
      .eq('pipeline_id', pipelineId)
      .eq('stage_role', role)
      .eq('ativo', true)
      .maybeSingle();
    if (error || !data) return null;
    return data as unknown as StageRoleInfo;
  };

  setStageRole: IFlowConfigRepository['setStageRole'] = async (stageId, role) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase não configurado');
    const { error } = await supabase
      .from('pipeline_stages')
      .update({ stage_role: role })
      .eq('id', stageId);
    if (error) throw error;
  };

  setStageEmpresaGate: IFlowConfigRepository['setStageEmpresaGate'] = async (stageId, value) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase não configurado');
    const { error } = await supabase
      .from('pipeline_stages')
      .update({ is_empresa_gate: value })
      .eq('id', stageId);
    if (error) throw error;
  };

  // ── Closer pipeline vinculado ─────────────────────────────────
  getPipelineBasic: IFlowConfigRepository['getPipelineBasic'] = async (id) => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data, error } = await supabase
      .from('pipelines')
      .select('id, workspace_id, funnel_id, closer_pipeline_id, role')
      .eq('id', id)
      .maybeSingle();
    if (error || !data) return null;
    return data as unknown as PipelineFull;
  };

  getActivePipelineById: IFlowConfigRepository['getActivePipelineById'] = async (id) => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase
      .from('pipelines')
      .select('*')
      .eq('id', id)
      .eq('ativo', true)
      .maybeSingle();
    return (data as unknown as PipelineFull) || null;
  };

  getCloserPipelineByFunnel: IFlowConfigRepository['getCloserPipelineByFunnel'] = async (workspaceId, funnelId) => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase
      .from('pipelines')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('role', 'closer')
      .eq('ativo', true)
      .eq('funnel_id', funnelId)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    return (data as unknown as PipelineFull) || null;
  };

  getAnyActiveCloserPipeline: IFlowConfigRepository['getAnyActiveCloserPipeline'] = async (workspaceId) => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase
      .from('pipelines')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('role', 'closer')
      .eq('ativo', true)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    return (data as unknown as PipelineFull) || null;
  };

  setCloserPipeline: IFlowConfigRepository['setCloserPipeline'] = async (sdrPipelineId, closerPipelineId) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase não configurado');
    const { error } = await supabase
      .from('pipelines')
      .update({ closer_pipeline_id: closerPipelineId || null })
      .eq('id', sdrPipelineId);
    if (error) throw error;
  };

  // ── Fluxo "ao concluir" ───────────────────────────────────────
  getCardOnCompleteInfo: IFlowConfigRepository['getCardOnCompleteInfo'] = async (cardId) => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase
      .from('cards')
      .select(`
        pipeline_id,
        stage_id,
        pipeline_stages!cards_stage_id_fkey ( name ),
        leads ( nome )
      `)
      .eq('id', cardId)
      .maybeSingle();
    const card = data as unknown as {
      pipeline_id: string | null;
      stage_id: string | null;
      pipeline_stages?: { name: string } | null;
      leads?: { nome: string | null } | null;
    } | null;
    if (!card) return null;
    return {
      pipeline_id: card.pipeline_id,
      stage_id:    card.stage_id,
      stage_name:  card.pipeline_stages?.name || null,
      lead_name:   card.leads?.nome || null,
    } as CardOnCompleteInfo;
  };

  getPipelineCadence: IFlowConfigRepository['getPipelineCadence'] = async (pipelineId) => {
    const supabase = getSupabase();
    if (!supabase) return [];
    const { data } = await supabase
      .from('pipelines')
      .select('cadence')
      .eq('id', pipelineId)
      .maybeSingle();
    return data && Array.isArray(data.cadence) ? (data.cadence as unknown[]) : [];
  };

  getStageNamesByIds: IFlowConfigRepository['getStageNamesByIds'] = async (ids) => {
    const supabase = getSupabase();
    if (!supabase) return [];
    const { data } = await supabase
      .from('pipeline_stages')
      .select('id, name')
      .in('id', ids);
    return (data || []) as unknown as StageName[];
  };

  getLeadName: IFlowConfigRepository['getLeadName'] = async (leadId) => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase
      .from('leads').select('nome').eq('id', leadId).maybeSingle();
    return data?.nome || null;
  };

  // ── Ações on_complete (mutations) ─────────────────────────────
  listPendingActivityIdsForCard: IFlowConfigRepository['listPendingActivityIdsForCard'] = async (cardId) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase indisponível');
    const { data, error } = await supabase
      .from('activities')
      .select('id')
      .eq('card_id', cardId)
      .eq('status', 'pending');
    if (error) throw error;
    return (data || []).map(a => a.id);
  };

  updateCardStage: IFlowConfigRepository['updateCardStage'] = async (cardId, stageId) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase indisponível');
    const { error } = await supabase
      .from('cards')
      .update({ stage_id: stageId })
      .eq('id', cardId);
    if (error) throw error;
  };

  cancelActivitiesByIds: IFlowConfigRepository['cancelActivitiesByIds'] = async (ids) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase indisponível');
    const { error } = await supabase
      .from('activities')
      .update({ status: 'cancelled' })
      .in('id', ids);
    if (error) throw error;
  };

  getLeadForCardCreation: IFlowConfigRepository['getLeadForCardCreation'] = async (leadId) => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase
      .from('leads').select('workspace_id, sdr_id, closer_id')
      .eq('id', leadId).maybeSingle();
    return (data as unknown as LeadCardCreationInfo) || null;
  };

  insertCard: IFlowConfigRepository['insertCard'] = async (cardData) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase indisponível');
    const { error } = await supabase.from('cards').insert(cardData);
    if (error) throw error;
  };

  insertActivity: IFlowConfigRepository['insertActivity'] = async (activityData) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase indisponível');
    const { error } = await supabase.from('activities').insert({
      ...activityData,
      status: 'pending',
    });
    if (error) throw error;
  };

  updateLead: IFlowConfigRepository['updateLead'] = async (leadId, patch) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase indisponível');
    const { error } = await supabase
      .from('leads').update(patch as never).eq('id', leadId);
    if (error) throw error;
  };

  findMeetingIdByStatus: IFlowConfigRepository['findMeetingIdByStatus'] = async (cardId, status) => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase
      .from('meetings').select('id')
      .eq('card_id', cardId)
      .eq('status', status as never)
      .order('scheduled_at', { ascending: false, nullsFirst: false })
      .limit(1).maybeSingle();
    return data?.id || null;
  };

  findLatestMeetingId: IFlowConfigRepository['findLatestMeetingId'] = async (cardId) => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase
      .from('meetings').select('id')
      .eq('card_id', cardId)
      .order('scheduled_at', { ascending: false, nullsFirst: false })
      .limit(1).maybeSingle();
    return data?.id || null;
  };

  updateMeetingStatus: IFlowConfigRepository['updateMeetingStatus'] = async (meetingId, status) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase indisponível');
    const { error } = await supabase
      .from('meetings').update({ status: status as never }).eq('id', meetingId);
    if (error) throw error;
  };

  updateCardReagend: IFlowConfigRepository['updateCardReagend'] = async (cardId, value) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase indisponível');
    const { error } = await supabase
      .from('cards').update({ reagend: value }).eq('id', cardId);
    if (error) throw error;
  };
}
